import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getTeam } from '../api';
import PageHeader from '../components/PageHeader';
import MatchList from '../components/MatchList';
import TeamPropsPanel from '../components/TeamPropsPanel';
import { stageLabel } from '../format';

const ROUND_LABELS: Record<string, string> = {
  group_md1: 'Group MD1', group_md2: 'Group MD2', group_md3: 'Group MD3',
  r32: 'R32', r16: 'R16', qf: 'QF', sf: 'SF', final: 'Final',
};

export default function TeamDetail() {
  const { code = '' } = useParams();
  const nav = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['team', code],
    queryFn: () => getTeam(code),
    enabled: !!code,
  });

  if (isLoading) return <PageHeader title="Team" />;
  if (error) return <div className="px-4 lg:px-0"><div className="card p-6 text-sm text-red-300">Failed: {(error as Error).message}</div></div>;
  if (!data) return null;

  const { team, matches, group, knockout, news, captains } = data;
  const upcoming = matches.filter((m) => m.status !== 'post');
  const completed = matches.filter((m) => m.status === 'post');

  return (
    <>
      <PageHeader
        title={`${team.flag} ${team.name}`}
        subtitle={
          <span>
            <button onClick={() => nav(-1)} className="text-pitch-400 hover:underline">← Back</button>
            {team.owner && (
              <span className="ml-2">
                Fantasy owner: <Link to={`/players/${encodeURIComponent(team.owner)}`} className="text-pitch-400 hover:underline">{team.owner}</Link>
              </span>
            )}
          </span>
        }
      />

      <div className="px-4 lg:px-0 space-y-4">
        {/* Quick stats: group standing + cup chance */}
        <section className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {group && group.row && (
            <div className="card p-4">
              <div className="text-xs uppercase tracking-widest text-dim mb-2">Group {group.label}</div>
              <div className="space-y-1.5">
                {group.teams.map((t, i) => {
                  const isMe = t.code === team.code;
                  const adv = i < 2;
                  return (
                    <div key={t.code} className={`flex items-center gap-2 text-sm ${isMe ? 'font-semibold text-pitch-400' : adv ? '' : 'opacity-60'}`}>
                      <span className={`inline-block w-1 h-4 rounded-sm ${adv ? 'bg-pitch-500' : 'bg-ink-700'}`} />
                      <span className="text-base leading-none">{t.flag}</span>
                      <span className="flex-1 truncate">{t.name}</span>
                      <span className="stat text-xs">{t.points}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {knockout && (
            <div className="card p-4">
              <div className="text-xs uppercase tracking-widest text-dim mb-2">Cup chance</div>
              <div className="grid grid-cols-3 gap-2 text-center">
                <Stat label="SF" value={pct(knockout.semiChance)} />
                <Stat label="Final" value={pct(knockout.finalChance)} />
                <Stat label="Cup" value={pct(knockout.cupChance)} accent />
              </div>
              <div className="text-[10px] text-dim mt-2 text-right">Strength {knockout.strength.toFixed(2)}</div>
            </div>
          )}
        </section>

        {/* Tournament-wide props */}
        <TeamPropsPanel code={team.code} />

        {/* Captains who picked this team */}
        {captains.length > 0 && (
          <section className="card p-4">
            <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Picked as captain by</h2>
            <div className="flex flex-wrap gap-1.5">
              {captains.map((c, i) => (
                <span key={i} className="pill bg-ink-800 text-chalk">
                  <Link to={`/players/${encodeURIComponent(c.user)}`} className="text-chalk hover:text-pitch-400">{c.user}</Link>
                  <span className="text-[10px] text-dim ml-1">{ROUND_LABELS[c.round] ?? c.round}</span>
                </span>
              ))}
            </div>
          </section>
        )}

        {/* Schedule */}
        <section>
          <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Upcoming</h2>
          <MatchList matches={upcoming} highlightCodes={[team.code]} showOwners emptyText="No upcoming matches." />
        </section>

        <section>
          <h2 className="text-xs uppercase tracking-widest text-dim mb-2">Completed</h2>
          <MatchList matches={completed} highlightCodes={[team.code]} showOwners emptyText="No completed matches yet." />
        </section>

        {/* News */}
        {news.length > 0 && (
          <section>
            <h2 className="text-xs uppercase tracking-widest text-dim mb-2">News about {team.name}</h2>
            <div className="space-y-2">
              {news.map((n) => (
                <a key={n.id} href={n.link} target="_blank" rel="noopener noreferrer" className="card flex gap-3 p-3 hover:border-pitch-500/40 transition-colors">
                  {n.image_url && <img src={n.image_url} alt="" className="w-20 h-16 object-cover rounded-lg shrink-0" loading="lazy" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-wider text-dim">{n.source} · {relative(n.published_at)}</div>
                    <h3 className="text-sm font-semibold leading-snug mt-0.5 line-clamp-2">{n.title}</h3>
                    {n.description && <p className="text-xs text-dim mt-0.5 line-clamp-2">{n.description}</p>}
                  </div>
                </a>
              ))}
            </div>
          </section>
        )}
      </div>
    </>
  );
}

function Stat({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <div className="rounded-lg bg-ink-800/80 px-2 py-2">
      <div className="text-[10px] uppercase tracking-wider text-dim">{label}</div>
      <div className={`stat text-sm font-semibold ${accent ? 'text-pitch-400' : ''}`}>{value}</div>
    </div>
  );
}

function pct(v: number): string {
  if (!v || !Number.isFinite(v)) return '—';
  if (v < 0.001) return '<0.1%';
  return `${(v * 100).toFixed(1)}%`;
}

function relative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
